/* eslint-disable react/jsx-no-literals */
/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */

import React from 'react';
import clsx from 'clsx';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import { OutputFrequency } from '../../types';
import styles from './formula-actions.scss';
import { FORMULA_ACTIONS, getFormulaActionsPlaceholder } from './const';

export interface FormulaActionsComponentProps {
  description?: string;
  onActionClick?: (action: string) => void;
  outputFrequency?: OutputFrequency;
  newStyling?: boolean;
}

export const FormulaActions = ({
  description,
  onActionClick,
  outputFrequency,
  newStyling
}: FormulaActionsComponentProps) => {
  const intl = useIntl();

  return (
    <>
      {description && (
        <div className={styles.description} data-testid="description">
          {description}
        </div>
      )}

      <div
        className={clsx(styles.actions, { [styles.newStyling]: newStyling })}
        onMouseDown={event => event.preventDefault()}
      >
        <div className={styles.actionsSection} data-testid="referenceSection">
          <div className={styles.actionTitle}>
            <FormattedMessage id="reporting.use_after_at_reference" defaultMessage="Use after an @ reference" />
          </div>
          <div className={styles.buttonContainer}>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('+')}>
              +
            </button>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('-')}>
              -
            </button>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('*')}>
              &times;
            </button>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('/')}>
              &divide;
            </button>
            {outputFrequency === OutputFrequency.daily && (
              <>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastMonthDaily])}
                >
                  <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastMonthDaily]}</div>}>
                    <div>
                      <FormattedMessage id="formula.last_month" defaultMessage="LASTMONTH" />
                    </div>
                  </Tooltip>
                </button>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearDaily])}
                >
                  <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearDaily]}</div>}>
                    <div>
                      <FormattedMessage id="formula.last_year" defaultMessage="LASTYEAR" />
                    </div>
                  </Tooltip>
                </button>
              </>
            )}
            {outputFrequency === OutputFrequency.monthly && (
              <>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastMonthMonthly])}
                >
                  <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastMonthMonthly]}</div>}>
                    <div>
                      <FormattedMessage id="formula.last_month" defaultMessage="LASTMONTH" />
                    </div>
                  </Tooltip>
                </button>
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearMonthly])}
                >
                  <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearMonthly]}</div>}>
                    <div>
                      <FormattedMessage id="formula.last_year" defaultMessage="LASTYEAR" />
                    </div>
                  </Tooltip>
                </button>
              </>
            )}
            {outputFrequency === OutputFrequency.annually && (
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearAnnually])}
              >
                <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.lastYearAnnually]}</div>}>
                  <div>
                    <FormattedMessage id="formula.last_year" defaultMessage="LASTYEAR" />
                  </div>
                </Tooltip>
              </button>
            )}
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => {
                if (outputFrequency === OutputFrequency.daily) {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateDaily]);
                } else if (outputFrequency === OutputFrequency.monthly) {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateMonthly]);
                } else {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateAnnually]);
                }
              }}
            >
              <Tooltip
                message={
                  <div>
                    {outputFrequency === OutputFrequency.daily ? (
                      <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateDaily]}</>
                    ) : outputFrequency === OutputFrequency.monthly ? (
                      <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateMonthly]}</>
                    ) : (
                      <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.onDateAnnually]}</>
                    )}
                  </div>
                }
              >
                <div>
                  <FormattedMessage id="formula.on_date" defaultMessage="ON DATE" />
                </div>
              </Tooltip>
            </button>
          </div>
        </div>

        <div className={styles.actionsSection} data-testid="conditionalsSection">
          <div className={styles.actionTitle}>
            <FormattedMessage id="predict.use_for_conditionals" defaultMessage="Use for conditionals" />
          </div>
          <div className={styles.buttonContainer}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.if])}
            >
              <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.if]}</div>}>
                <div>
                  <FormattedMessage id="formula.if" defaultMessage="IF" />
                </div>
              </Tooltip>
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.year])}
            >
              <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.year]}</div>}>
                <div>{intl.formatMessage({ id: 'formula.year', defaultMessage: 'year' }).toUpperCase()}</div>
              </Tooltip>
            </button>
            {(outputFrequency === OutputFrequency.daily || outputFrequency === OutputFrequency.monthly) && (
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.month])}
              >
                <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.month]}</div>}>
                  <div>{intl.formatMessage({ id: 'common.month', defaultMessage: 'month' }).toUpperCase()}</div>
                </Tooltip>
              </button>
            )}
            {outputFrequency === OutputFrequency.daily && (
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dayOfMonth])}
              >
                <Tooltip message={<div>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dayOfMonth]}</div>}>
                  <div>
                    <FormattedMessage id="formula.day_of_month" defaultMessage="DAYOFMONTH" />
                  </div>
                </Tooltip>
              </button>
            )}
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => {
                if (outputFrequency === OutputFrequency.daily) {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateDaily]);
                } else if (outputFrequency === OutputFrequency.monthly) {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateMonthly]);
                } else {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateAnnually]);
                }
              }}
            >
              <Tooltip
                message={
                  <div>
                    {outputFrequency === OutputFrequency.daily ? (
                      <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateDaily]}</>
                    ) : outputFrequency === OutputFrequency.monthly ? (
                      <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateMonthly]}</>
                    ) : (
                      <>{getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.dateAnnually]}</>
                    )}
                  </div>
                }
              >
                <div>
                  <FormattedMessage id="formula.date" defaultMessage="DATE" />
                </div>
              </Tooltip>
            </button>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('<')}>
              &lt;
            </button>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('>')}>
              &gt;
            </button>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('<=')}>
              &le;
            </button>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('>=')}>
              &ge;
            </button>
            <button type="button" className={clsx(styles.actionButton, styles.operator)} onClick={() => onActionClick?.('=')}>
              =
            </button>
          </div>
        </div>

        <div className={styles.actionsSection} data-testid="functionsSection">
          <div className={styles.actionTitle}>
            <FormattedMessage id="predict.functions" defaultMessage="Functions" />
          </div>
          <div className={styles.buttonContainer}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => {
                if (outputFrequency === OutputFrequency.daily) {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumDaily]);
                } else if (outputFrequency === OutputFrequency.monthly) {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumMonthly]);
                } else {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumAnnually]);
                }
              }}
            >
              <Tooltip
                message={
                  <div>
                    {outputFrequency === OutputFrequency.daily
                      ? getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumDaily]
                      : outputFrequency === OutputFrequency.monthly
                      ? getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumMonthly]
                      : getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.sumAnnually]}
                  </div>
                }
              >
                <div>
                  <FormattedMessage id="formula.sum" defaultMessage="SUM" />
                </div>
              </Tooltip>
            </button>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => {
                if (outputFrequency === OutputFrequency.daily) {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgDaily]);
                } else if (outputFrequency === OutputFrequency.monthly) {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgMonthly]);
                } else {
                  onActionClick?.(getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgAnnually]);
                }
              }}
            >
              <Tooltip
                message={
                  <div>
                    {outputFrequency === OutputFrequency.daily
                      ? getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgDaily]
                      : outputFrequency === OutputFrequency.monthly
                      ? getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgMonthly]
                      : getFormulaActionsPlaceholder(intl)[FORMULA_ACTIONS.avgAnnually]}
                  </div>
                }
              >
                <div>
                  <FormattedMessage id="formula.avg" defaultMessage="AVG" />
                </div>
              </Tooltip>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
